import React, { useState } from 'react';
import { DownloadFileResponse, useGetUserDownloadsQuery } from '../../services/downloadsApi';
import Loader from '../../components/loader';
import auth from '../../components/auth';
import getConfig from '../../app/config';

const DownloadsPage: React.FC = () => {
  const { data: download, isLoading, error } = useGetUserDownloadsQuery();

  const {files, errors} = download ??  {files: [], errors: null};

  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (item: DownloadFileResponse) => {
    if (!item || !item.id) return;
    const clientId = auth.getUserClientId();
    if (!clientId) {
      alert('Missing client id');
      return;
    }

    setDownloadingId(item.id);
    try {        
      const apiUrl = getConfig().apiUrl;
      const tokenResp = await fetch(`${apiUrl}download/export/${item.id}/token`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${auth.getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!tokenResp.ok) {
        const err = await tokenResp.text();
        throw new Error(err || 'Failed to get download token');
      }

      const tokenData = await tokenResp.json();
      const token = tokenData?.token ?? tokenData?.t ?? tokenData;
      const downloadUrl = `${getConfig().apiUrl}download/export/${item.id}?token=${encodeURIComponent(token)}`;
      
      // request the file and download it (supports Authorization header)
      const resp = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${auth.getToken()}` // include if needed
        }
      });
      if (!resp.ok) throw new Error('Download failed');

      const blob = await resp.blob();

      // try to parse filename from Content-Disposition header, fallback to item.fileName
      const cd = resp.headers.get('content-disposition') || '';
      const m = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      const filename = m ? m[1].replace(/['"]/g, '') : item.fileName || 'download';

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      console.error('Download token error', e);
      alert(e?.message || 'Failed to get download token');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="container">
      <div className="columns is-centered">
        <div className="column is-8-tablet is-6-desktop">
          <div className="box">
            <h4 className="title is-4">Download Exported Files</h4>
            
            {isLoading && <Loader />}
            
            {error && (
              <div className="notification is-danger">
                {error instanceof Error ? error.message : 'Failed to load downloads.'}
              </div>
            )}
            
            {!isLoading && !error && (
              <>
                {files.length === 0 ? (
                  <div className="notification is-info is-light">
                    No downloads found.
                  </div>
                ) : (
                  <div className="table-container">
                    <table className="table is-fullwidth is-striped is-hoverable">
                      <thead>
                        <tr>
                          <th>File Name</th>
                          <th>Created</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {files.map((item: DownloadFileResponse) => (
                          <tr key={item.id}>
                            <td>{item.fileName}</td>
                            <td>{new Date(item.created).toLocaleString()}</td>
                            <td>
                              <button
                                className="button is-small is-primary"
                                onClick={() => handleDownload(item)}
                                disabled={downloadingId === item.id}
                              >
                                {downloadingId === item.id ? 'Preparing...' : 'Download'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadsPage;
